import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { AuthAlert, AuthPageLayout } from '../../auth/components/AuthPageLayout';
import { useAuth } from '../../auth/context/AuthContext';
import { Spinner } from '../../../shared/components';
import { getAppHostUrl } from '../../../shared/config/workspaceHost';
import styles from './WorkspacePicker.module.css';

/**
 * Landing page on the workspace host after the global picker. It trades the one-time code for a
 * normal tenant session, which is what every other page in the app expects to find.
 */
export const WorkspaceHandoffPage: React.FC = () => {
  const { t } = useTranslation('workspaces');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { completeWorkspaceHandoff } = useAuth();
  const [error, setError] = React.useState<string | null>(null);

  const code = searchParams.get('code');
  const tenantId = searchParams.get('tenantId');

  // The code is single-use, so a repeated effect run must not spend it a second time.
  const startedRef = React.useRef(false);

  React.useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;

    if (!code) {
      setError(t('errors.handoffMissingCode'));
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        await completeWorkspaceHandoff(code, tenantId);
        if (!cancelled) {
          navigate('/', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : t('errors.handoffFailed'));
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [code, completeWorkspaceHandoff, navigate, t, tenantId]);

  const appHostUrl = getAppHostUrl();

  return (
    <AuthPageLayout title={t('handoff.title')} subtitle={error ? undefined : t('handoff.subtitle')}>
      {error ? (
        <>
          <AuthAlert variant="error">{error}</AuthAlert>
          <p className={styles.footer}>
            {appHostUrl ? (
              <a className={styles.linkButton} href={`${appHostUrl}/login`}>
                {t('handoff.backToGlobalLogin')}
              </a>
            ) : (
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => navigate('/login', { replace: true })}
              >
                {t('handoff.backToLogin')}
              </button>
            )}
          </p>
        </>
      ) : (
        <div className={styles.status}>
          <Spinner size={36} text={t('handoff.working')} />
        </div>
      )}
    </AuthPageLayout>
  );
};

export default WorkspaceHandoffPage;
